// src/features/inventory/pages/ItemDetailPage/tabs/BatchNumbers/AddBatchDialog.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { lotsAPI } from '../../../../services/lots';
import { useApi } from '../../../../../../hooks/api/useApi';
import { useStockLocations } from '../../../../hooks/useStockLocations';
import ReceiptCostFields, { parseUnitCost } from '../../../../components/ReceiptCostFields';

const EMPTY_FORM = {
  lot_number: '',
  initial_quantity: '',
  expiry_date: '',
  production_date: '',
  notes: '',
  location_id: '',
};

export default function AddBatchDialog({ open, onClose, onSuccess, itemId, uom }) {
  const { t } = useTranslation(['inventory', 'common']);
  const { loading, error, execute, clearError } = useApi();
  const [form, setForm] = useState(EMPTY_FORM);
  const [unitCost, setUnitCost] = useState('');
  const [updatePrice, setUpdatePrice] = useState(true);
  const { stockLocations, fetchStockLocations } = useStockLocations();

  useEffect(() => {
    if (open) fetchStockLocations();
  }, [open, fetchStockLocations]);

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleClose = () => {
    setForm(EMPTY_FORM);
    setUnitCost('');
    setUpdatePrice(true);
    clearError();
    onClose();
  };

  const isValid = form.lot_number.trim() && form.initial_quantity !== ''
    && (unitCost === '' || parseUnitCost(unitCost) != null);

  const handleSubmit = async () => {
    if (!isValid) return;
    const result = await execute(() =>
      lotsAPI.create({
        item_id: itemId,
        lot_number: form.lot_number.trim(),
        initial_quantity: parseInt(form.initial_quantity, 10),
        expiry_date: form.expiry_date || null,
        production_date: form.production_date || null,
        notes: form.notes.trim() || null,
        location_id: form.location_id || null,
        unit_cost: parseUnitCost(unitCost),
        update_cost_price: updatePrice,
      })
    );
    if (result.success) {
      onSuccess();
      handleClose();
    }
  };

  const activeLocations = stockLocations.filter(l => l.active);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
          {t('inventory:items.tabs.batches.dialog.title')}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          {error && (
            <Alert severity="error" onClose={clearError}>{error}</Alert>
          )}

          <TextField
            label={t('inventory:items.tabs.batches.columns.lot_number')}
            value={form.lot_number}
            onChange={set('lot_number')}
            required
            disabled={loading}
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />

          <TextField
            label={t('inventory:items.tabs.batches.dialog.initial_quantity')}
            type="number"
            value={form.initial_quantity}
            onChange={set('initial_quantity')}
            required
            disabled={loading}
            slotProps={{ htmlInput: { min: 0, step: 1 } }}
          />

          <ReceiptCostFields
            open={open}
            itemId={itemId}
            uom={uom}
            value={unitCost}
            onChange={setUnitCost}
            updatePrice={updatePrice}
            onUpdatePriceChange={setUpdatePrice}
            disabled={loading}
          />

          {activeLocations.length > 0 && (
            <TextField
              select
              label={t('inventory:items.tabs.batches.dialog.location')}
              value={form.location_id}
              onChange={set('location_id')}
              disabled={loading}
            >
              <MenuItem value="">
                <Typography color="text.secondary" variant="body2">
                  {t('inventory:items.tabs.batches.dialog.no_location')}
                </Typography>
              </MenuItem>
              {activeLocations.map((loc) => (
                <MenuItem key={loc.stock_location_id} value={loc.stock_location_id}>
                  {loc.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Box width="100%" display="flex" gap={3}>
            <TextField
              label={t('inventory:items.tabs.batches.dialog.production_date')}
              type="date"
              value={form.production_date}
              onChange={set('production_date')}
              disabled={loading}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <TextField
              label={t('inventory:items.tabs.batches.columns.expiry_date')}
              type="date"
              value={form.expiry_date}
              onChange={set('expiry_date')}
              disabled={loading}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Box>

          <TextField
            label={t('inventory:items.tabs.batches.dialog.notes')}
            value={form.notes}
            onChange={set('notes')}
            multiline
            rows={3}
            disabled={loading}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('common:buttons.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? t('common:buttons.saving') : t('inventory:items.tabs.batches.dialog.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
